import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Undo2, AlertTriangle, MapPin, ChevronDown, ChevronUp } from 'lucide-react';
import { slotWhere } from '../../../utils/nurseryRowSlots';
import type { FreeLineGroup } from '../../../utils/pickCustomers';
import LotBands from '../LotBands';
import type { LotBand } from '../../../types/nurseryColour';
import Button from '../../ui/Button';
import nurseryService from '../../../services/nursery';
import { errorMessage } from '../../../utils/apiResponse';
import { cn } from '../../../utils/cn';
import { TagSwatch } from '../TagSwatch';
import FreePickLineCard from './FreePickLineCard';
import { SHORT_REASON_LABELS, type FreePickLine, type PickList, type PickShortReason } from '../../../types/nursery';

/**
 * The same tree on several of one customer's order lines, on a load picked
 * without allocating, as one card. "Take N" at a place takes what all the
 * lines still want, filling the oldest order first; each line keeps its own
 * pick, so each order is invoiced on its own. Typing a number, correcting a
 * count or opening a line again is done per order, under "Each order
 * separately".
 */

export interface CombinedFreeLineCardProps {
  group: FreeLineGroup;
  loadId: number;
  despatchFarmName: string;
  editable: boolean;
  onChanged: (list: PickList) => void;
  highlightedLotId?: number | null;
  bandsByLot?: Record<string, LotBand[]>;
}

const REASONS = Object.keys(SHORT_REASON_LABELS) as PickShortReason[];
const PLACES_SHOWN = 4;
const inputCls = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

const tookFrom = (ln: FreePickLine, lotId: number) => ln.lots.find((l) => l.lot_id === lotId)?.pick?.quantity_picked ?? 0;

export function CombinedFreeLineCard({ group, loadId, despatchFarmName, editable, onChanged, highlightedLotId, bandsByLot }: CombinedFreeLineCardProps) {
  const { t } = useTranslation('nursery', { keyPrefix: 'CombinedFreeLineCard' });
  const { lines } = group;
  const first = lines[0];
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [closing, setClosing] = useState(false);
  const [reason, setReason] = useState<PickShortReason | ''>('');
  const [notes, setNotes] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [separately, setSeparately] = useState(false);

  const ordered = lines.reduce((s, ln) => s + ln.quantity_ordered, 0);
  const picked = lines.reduce((s, ln) => s + ln.quantity_picked, 0);
  const open = lines.filter((ln) => ln.status === 'open');
  const remaining = open.reduce((s, ln) => s + ln.remaining, 0);
  const done = lines.every((ln) => ln.status === 'picked');
  const anyShort = lines.some((ln) => ln.status === 'short');
  const freeElsewhere = Math.max(...lines.map((ln) => ln.free_elsewhere));

  // Every line has the same lots; the first line's list gives the places and what each has free.
  const places = first.lots.filter((l) => l.free > 0 || lines.some((ln) => tookFrom(ln, l.lot_id) > 0));
  const highlightedIndex = highlightedLotId != null ? places.findIndex((l) => l.lot_id === highlightedLotId) : -1;
  const visible = showAll || highlightedIndex >= PLACES_SHOWN ? places : places.slice(0, PLACES_SHOWN);

  const each = async (calls: (() => Promise<{ success: boolean; data?: PickList } & object>)[], fallback: string) => {
    setBusy(true);
    setError(null);
    let last: PickList | null = null;
    try {
      for (const call of calls) {
        const res = await call();
        if (!res.success || !res.data) {
          setError(errorMessage(res, fallback));
          return false;
        }
        last = res.data;
      }
      return true;
    } catch (e) {
      setError(errorMessage(e, fallback));
      return false;
    } finally {
      if (last) onChanged(last);
      setBusy(false);
    }
  };

  /** All this place can give, shared across the lines still wanting trees, oldest order first. */
  const takeAll = (lotId: number, free: number) => {
    let left = free;
    const calls: (() => Promise<{ success: boolean; data?: PickList }>)[] = [];
    for (const ln of open) {
      const take = Math.min(left, ln.remaining);
      if (take <= 0) continue;
      left -= take;
      const quantity = tookFrom(ln, lotId) + take;
      calls.push(() => nurseryService.setFreePick(loadId, ln.order_line_id, lotId, { quantity }));
    }
    return each(calls, t('pickFailed'));
  };

  const undo = (lotId: number) => each(
    lines.filter((ln) => tookFrom(ln, lotId) > 0)
      .map((ln) => () => nurseryService.setFreePick(loadId, ln.order_line_id, lotId, { quantity: 0 })),
    t('pickFailed'),
  );

  const closeShort = async () => {
    if (!reason) { setError(t('chooseReason')); return; }
    const ok = await each(
      open.map((ln) => () => nurseryService.closeFreeLineShort(loadId, ln.order_line_id, { short_reason: reason, notes: notes || null })),
      t('closeFailed'),
    );
    if (ok) { setClosing(false); setReason(''); setNotes(''); }
  };

  const orderLine = (ln: FreePickLine) => {
    const state = ln.status === 'picked' ? t('orderPicked')
      : ln.status === 'short' ? t('orderShort', { picked: ln.quantity_picked, ordered: ln.quantity_ordered })
        : ln.quantity_picked > 0 ? t('orderSoFar', { picked: ln.quantity_picked }) : null;
    return [t('orderQty', { order: ln.order_number, n: ln.quantity_ordered }), state].filter(Boolean).join(' · ');
  };

  return (
    <li id={`free-group-${group.key}`} className={cn('rounded-xl border bg-white p-4',
      done ? 'border-green-200 bg-green-50/40' : anyShort ? 'border-amber-200 bg-amber-50/40' : 'border-neutral-200')}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-neutral-900 break-words">
            {first.sold_as ?? `${first.cultivar_name}${first.rootstock_name ? ` / ${first.rootstock_name}` : ''}`}
          </p>
          <p className="text-sm text-neutral-600 break-words">{first.tree_type_name}</p>
          {first.sold_as && (first.tag_colour_hex || first.tag_colour_name) && (
            <p className="mt-0.5 flex items-center gap-1.5 text-xs text-neutral-600 break-words">
              <TagSwatch hex={first.tag_colour_hex} name={first.tag_colour_name} />
              <span>{first.tag_colour_name ? t('tag', { colour: first.tag_colour_name }) : t('tagColour')}</span>
            </p>
          )}
          <p className="mt-1 inline-block rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-800 max-w-full break-words">
            {t('combined', { count: lines.length })}
          </p>
          <ul className="mt-1 space-y-1" aria-label={t('orders')}>
            {lines.map((ln) => (
              <li key={ln.order_line_id} className="text-xs text-neutral-600 break-words">
                {orderLine(ln)}
                {ln.line_notes && (
                  <span className="mt-0.5 block rounded bg-blue-50 border border-blue-200 px-2 py-1 text-sm text-blue-900 break-words">
                    {ln.line_notes}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-2xl font-bold text-neutral-900 leading-none">
            {picked > 0 ? `${picked}/${ordered}` : ordered}
          </p>
          <p className="text-xs text-neutral-500">{picked > 0 ? t('picked') : t('toPick')}</p>
        </div>
      </div>

      {done && (
        <p className="mt-3 flex items-center gap-1.5 text-sm text-green-800 font-medium">
          <Check className="h-4 w-4" /> {t('allPicked', { n: ordered })}
        </p>
      )}

      {!separately && (places.length === 0 ? (
        <p className="mt-3 flex items-start gap-2 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span className="break-words min-w-0">
            {t('noStockHere', { farm: despatchFarmName })}
            {freeElsewhere > 0 ? ` ${t('freeElsewhere', { count: freeElsewhere })}` : ''}
          </span>
        </p>
      ) : (
        <ul className="mt-3 space-y-2" aria-label={t('whereItStands')}>
          {visible.map((lot) => {
            const took = lines.reduce((s, ln) => s + tookFrom(ln, lot.lot_id), 0);
            const canTake = editable && lot.free > 0 && remaining > 0;
            const what = first.sold_as && lot.cultivar_name ? [lot.cultivar_name, lot.rootstock_name].filter(Boolean).join(' / ') : null;
            const n = Math.min(remaining, lot.free);
            return (
              <li key={lot.lot_id}
                className={cn('rounded-lg border p-3 text-sm', took ? 'border-green-200 bg-white' : 'border-neutral-200',
                  highlightedLotId === lot.lot_id && 'ring-2 ring-primary-500')}>
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-neutral-400 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-neutral-900 break-words">{slotWhere(lot)}</p>
                    {bandsByLot?.[lot.lot_id]?.length ? <LotBands bands={bandsByLot[lot.lot_id]} compact /> : null}
                    <p className="text-xs text-neutral-600 break-words">
                      {lot.lot_code ? t('lotCode', { code: lot.lot_code }) : t('lotNumber', { id: lot.lot_id })}
                      {what ? ` · ${what}` : ''}
                    </p>
                    {took > 0 && <p className="text-xs text-green-800 font-medium mt-0.5">{t('took', { n: took })}</p>}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-semibold text-neutral-900">{lot.free}</p>
                    <p className="text-xs text-neutral-500">{t('free')}</p>
                  </div>
                </div>
                {editable && (canTake || took > 0) && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {canTake && (
                      <Button size="sm" disabled={busy} onClick={() => takeAll(lot.lot_id, lot.free)} className="max-w-full">
                        <Check className="h-4 w-4 flex-shrink-0" />
                        <span className="truncate">{took ? t('takeMore', { n }) : t('take', { n })}</span>
                      </Button>
                    )}
                    {took > 0 && (
                      <Button variant="outline" size="sm" disabled={busy} onClick={() => undo(lot.lot_id)}>
                        <Undo2 className="h-4 w-4" /> {t('undo')}
                      </Button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
          {places.length > visible.length && (
            <li>
              <Button variant="ghost" size="sm" onClick={() => setShowAll(true)}>
                <ChevronDown className="h-4 w-4" /> {t('showMore', { count: places.length - visible.length })}
              </Button>
            </li>
          )}
          {showAll && places.length > PLACES_SHOWN && (
            <li>
              <Button variant="ghost" size="sm" onClick={() => setShowAll(false)}>
                <ChevronUp className="h-4 w-4" /> {t('showFewer')}
              </Button>
            </li>
          )}
        </ul>
      ))}

      {error && <p className="mt-3 text-sm text-red-700 break-words">{error}</p>}

      {editable && !closing && (
        <div className="mt-3 flex flex-wrap gap-2">
          {!separately && open.length > 0 && remaining > 0 && (
            <Button variant="outline" size="sm" disabled={busy} onClick={() => { setClosing(true); setError(null); }}>
              {t('cantFindRest')}
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => setSeparately(!separately)} aria-expanded={separately}>
            {separately ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            {t('separately')}
          </Button>
        </div>
      )}

      {separately && (
        <ul className="mt-2 space-y-3">
          {lines.map((ln) => (
            <FreePickLineCard
              key={ln.order_line_id}
              line={ln}
              loadId={loadId}
              despatchFarmName={despatchFarmName}
              editable={editable}
              onChanged={onChanged}
              highlightedLotId={highlightedLotId}
              bandsByLot={bandsByLot}
            />
          ))}
        </ul>
      )}

      {closing && (
        <div className="mt-4 space-y-3 border-t border-neutral-100 pt-3">
          <p className="text-sm text-neutral-800 break-words">
            {t('closeNote', { picked, ordered, rest: remaining })}
          </p>
          <fieldset>
            <legend className="block text-sm font-medium text-neutral-700 mb-1">{t('why')}</legend>
            <div className="space-y-1">
              {REASONS.map((r) => (
                <label key={r} className="flex items-center gap-2 text-sm text-neutral-700">
                  <input type="radio" name={`free-g-reason-${first.order_line_id}`} checked={reason === r} onChange={() => setReason(r)} />
                  {t(`shortReason.${r}`, { defaultValue: SHORT_REASON_LABELS[r] })}
                </label>
              ))}
            </div>
          </fieldset>
          <div>
            <label htmlFor={`free-g-notes-${first.order_line_id}`} className="block text-sm font-medium text-neutral-700 mb-1">{t('notes')}</label>
            <input id={`free-g-notes-${first.order_line_id}`} value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} />
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" size="sm" disabled={busy} onClick={() => { setClosing(false); setError(null); }}>{t('cancel')}</Button>
            <Button size="sm" disabled={busy} onClick={closeShort}>{busy ? t('saving') : t('closeLines')}</Button>
          </div>
        </div>
      )}
    </li>
  );
}

export default CombinedFreeLineCard;
